/**
 * @memberof SMART2
 * @ngdoc directive
 * @name Typeahead
 * @description This directive is used for creating a typeahead component ie Type and search plus select.
 * 
 * @attr {String} label
 *    Label to be displayed for identification of this ui element
 * @attr {String} placeholder
 *    Hint text to be displayed
 * @attr {String} info-icon
 *    To show a custom icon
 * @attr {Array} ng-model
 *    Default values to be displayed (Note: Pass array only if multiselect is set to true. Else pass an object.)
 *  @attr {Array} options
 *    Values to be shown in suggestions
 * @attr {Boolean} multiselect
 *    To allow/disallow multiple selections
 * @attr {Boolean} addnew
 *    To allow add new option
 * @attr {Boolean} show-lookup
 *    To make autocomplete plus lookup available
 * @attr {Boolean} focus 
 *    If set to true, this ui element will be focused
 * @attr {Boolean} validate
 *    If set to true, this ui element will be validated on the basis of rules passed to it
 * @attr {Array} rules 
 *    Rules to be evaluated when this element's blur event is fired. A rule must have 'rule' and 'errorMessage' properties (keys).
 *    'rule' must be a condition or group of conditions. 'errorMessage' will be the message to be displayed when corresponding rule fails. See example for more.
 * @attr {String} error-message 
 *    Error message to be displayed. This attribute can be set at any point to display an error message.
 * @attr {Boolean} is-mandatory 
 *    If set to true, default error message will be displayed when blur event is fired and this ui element is left blank
 * @attr {Boolean} disable 
 *    If set to true, field cannot be edited. Blur and focus events will not be fired (default behaviour).
 * @attr {Function} on-focus
 *    Callback function when ui element's focus event is fired
 * @attr {Function} on-blur
 *    Callback function when ui element's blur event is fired
 * @attr {Function} on-key-up
 *    Callback function when ui element's keyup event is fired
 * @attr {Function} on-key-down
 *    Callback function when ui element's keydown event is fired
 * @attr {Function} on-key-press
 *    Callback function when ui element's keypress event is fired
 * @attr {Function} on-select
 *    Callback function when a value is selected 
 * @attr {Function} callback-on-iconclick
 *    Callback function for icon on the right side
 * @attr {Function} call-addnew
 *    Callback function for add new icon
 * 
 *    
 * @example
 Static:
 Controller:
      $scope.options = [
		{
			"UserId": 28360,
			"UserName": "SRUser1@outlook.com",                  
			"FirstName": "SR",                  
			"LastName": "User1"
		}, {                 
			"UserId": 28977,                  
			"UserName": "SRUser1@outlook.com11",
			"FirstName": "Test",
			"LastName": "TestLastName"    
		}, {                  
			"UserId": 57900,                  
			"UserName": "SRUser1@outlook.com234",                  
			"FirstName": "Test23",                  
			"LastName": "test23lastname"                 
		}
	]; 
	$scope.selected = [{            
		"UserId": 28360,               
		"UserName": "SRUser1@outlook.com",               
		"FirstName": "SR",               
		"LastName": "User1"              
	}];  
 Usage:
      <smart-typeahead class="col s2" multiselect="true" addnew="true" showlookup="true" placeholder="Place Holder" ng-model="selected" options="options" filterkeys="['FirstName', 'LastName']" optionformat="<span>{FirstName} {LastName}</span>" displayformat="{FirstName} {LastName}" label="{{typeaheadLabel}}" callback-on-iconclick="callAfterClickedOnRightSideIcon(e)" info-icon="#icon_CirclePlus" call-addnew="callAfterAddNewFuncClick(e)"></smart-typeahead>       
 */
(function () {
    'use strict';
    var typeaheadId = 0;
    angular.module('SMART2').directive('smartTypeahead', ['$rootScope', '$timeout', '$translate', 'ScrollTo', 'lookup', 'RuleEngine', '$window', function ($rootScope, $timeout, $translate, ScrollTo, lookup, RuleEngine, $window) {
        return {
            restrict: 'E',
            replace: true,
            require: 'ngModel',
            scope: {
                label: '@',
                ngModel: '=?',
                options: '=?',
                isMandatory: '@',
                isVisible: '=?',
                rules: '@',
                validate: '=?',
                focus: '=?',
                parentElement: '@',
                onChange: '&',
                onFocus: '&',
                onBlur: '&',
                onKeyUp: '&',
                onKeyDown: '&',
                onKeyPress: '&',
                onEnter: '&',
                onSelect: '&',
                callbackOnIconClick: '&',
                callAddNew: '&',
                lookupOpen: '&',
                lookupHide: '&',
                onScrollEnd: '&',
                onScrollTop: '&',
                onScrollInProgress: '&',
                searchtextKeyUp: '&',
                errorMessage: '@',
                //  input element's attribute except for listeners should be in small letters
                align: '@',
                format: '@',    //  date format
                readonly: '@',
                disable: '@',
                prefixicon: '@',
                maxlength: '@',
                charactercounter: '@',
                decimalprecision: '@',
                minmaxprecision: '@',
                placeholder: '@',
                autocomplete: '@',
                removable: '@',
                datakey: '@',
                filterkeys: '@',
                optionformat: '@',
                displayformat: '@',
                multiselect: '@',
                showLookup: '@',
                infoIcon: '@',
                infoIconTooltip: '@',
                addnew: '@',
                titleofmodel: '@',
                selecttypeoption: '=?',
                showInfoIcon: '=?',
                isSupplier: '=?',
                defaultselectoption: '@',
                defaultselectiontext: '=?',
                width: '@',
                height: '@',
                isDefaultMandatory: '@',
                minCharForTrigger: '@',
                noSuggestionNotice: '@',
                showNoSuggestion: '@',
                errorModel: '=?',
                loading: '=?',
                minMultiselect: '@',
                showinfo: '@',
                showwarning: '@',
                clienterror: '=?',
                isLazyLoadingEnabled: '@',
                stopFilterOnKey: '@',
                searchCount: '@',
                requireLoader: '@',
		enablePagination:'@'
            },
            link: function (scope, element, attrs, ngModel) {
                scope.placeholder = (scope.placeholder == undefined || scope.placeholder == null) ? '' : scope.placeholder;
                scope.ngModel = (scope.ngModel == undefined || scope.ngModel == null) ? '' : scope.ngModel;
                scope.label = (scope.label == undefined || scope.label == null) ? '' : scope.label;
                scope.textAlign = (scope.align == undefined || scope.align == null) ? 'left' : scope.align;
                scope.infoIconFlag = (scope.infoIcon == undefined || scope.infoIcon == null) ? false : true;
                scope.titleofmodel = (scope.titleofmodel == undefined || scope.titleofmodel == null) ? $translate.instant('titleOfModel') : scope.titleofmodel;
                scope.infoIconTooltip = (scope.infoIconTooltip == undefined || scope.infoIconTooltip == null) ? '' : scope.infoIconTooltip;
                scope.isLazyLoadingEnabled = angular.isDefined(scope.isLazyLoadingEnabled) ? scope.isLazyLoadingEnabled : false;
                var multiselect = (scope.multiselect == undefined || scope.multiselect == null) ? false : scope.multiselect == "true" ? true : false;
                var minMultiselect = (scope.minMultiselect == undefined || scope.minMultiselect == null) ? { event: false } : { event: true, value: scope.minMultiselect };
                var showLookup = (scope.showLookup == undefined || scope.showLookup == null) ? true : scope.showLookup == "true" ? true : false;
                var addnew = (scope.addnew == undefined || scope.addnew == null) ? false : scope.addnew == "true" ? true : false;
                var readonly = (scope.readonly == undefined || scope.readonly == null) ? false : scope.readonly == "true" ? true : false;
                var minCharForTrigger = scope.minCharForTrigger ? parseInt(scope.minCharForTrigger) : 3;
                var noSuggestionNotice = angular.isDefined(scope.noSuggestionNotice) ? scope.noSuggestionNotice : $translate.instant('noResultsErrMsg');
                var showNoSuggestion = angular.isDefined(scope.showNoSuggestion) ? (scope.showNoSuggestion == 'true') ? true : false : false;
                var loading = angular.isDefined(scope.loading) ? scope.loading : false;
                showNoSuggestion = (noSuggestionNotice == '') ? false : showNoSuggestion;
                var number, rulesValidationEvent;
                var showLookupKey = $translate.instant('showLookupLabel'),
                    addNewKey = $translate.instant('addNew');
                scope.disabled = "";
                scope.showInfoIcon = (scope.showInfoIcon == undefined || scope.showInfoIcon == null) ? false : scope.showInfoIcon;
                var isInfoTipEnable = attrs.showInfoTip == 'true' ? true : false,
                    checkInfoToolTip = function () {
                        if (isInfoTipEnable) {
                            scope.infotipData = scope.displayModel.replace(/,/g, '  ').trim().replace(/  /g, '<br>');
                        } else {
                            scope.infotipData = "";
                        }
                    };

                var selectedArr = [], newOptionsSelected = [], newArr = [], copyArray = [], selectedArrIndex;
                if (readonly) {
                    scope.disabled = "disabled";
                }
                var uniqueIDGenerator = function () {
                    var d = new Date().getTime();
                    var uniqueID = 'xx2xxpxxoxx'.replace(/[xy]/g, function (c) {
                        var r = (d + Math.random() * 16) % 16 | 0;
                        d = Math.floor(d / 16);
                        return (c == 'x' ? r : (r & 0x3 | 0x8)).toString(16);
                    });
                    return uniqueID;
                };

                function checkIfActive() {
                    if (scope.placeholder === '' && (scope.displayModel === '' || scope.displayModel === undefined || scope.displayModel === null)) {
                        return false;
                    }
                    return true;
                }
                scope.isActive = checkIfActive();
                //  following line of code has been added for pendo
                var tmpId = '';     
                var typeaheadId = uniqueIDGenerator();        
                if (attrs.id) {
                    tmpId  = "tyh_" + attrs.id;        
                    var tmpElementCount = $('#' + tmpId).length;
                    if(tmpElementCount > 0) {
                        tmpId  = tmpId + '_' + typeaheadId;
                    }
                    element.removeAttr('id');
                    element.attr("id",  tmpId + '_container');
                }
                else {                   
                    tmpId = "tyh_" + typeaheadId;
                    element.removeAttr('id');
                    element.attr("id",  tmpId + '_container');   
                }
                scope.typeaheadId = tmpId;
                scope.labelFortypeaheadId = 'label-for-' + scope.typeaheadId;

                /*
				* Skip tab key focus, if field is readonly
				*/
                $timeout(function () {
                    if (readonly) {
                        angular.element('#' + scope.typeaheadId).attr('tabindex', -1);
                    }
                });

                var unbindFocus = scope.$watch('focus', function (newVal, oldVal) {
                    if (newVal) {
                        ScrollTo.perform(element, angular.element('#' + scope.parentElement));
                        setTimeout(function () {
                            angular.element('#' + scope.labelFortypeaheadId).trigger('click');
                        });
                        scope.focus = false;
                    }
                });

                var changeTimeout, keyUpTimeout, keyDownTimeout, keyPressTimeout;
                scope.ngChange = function (e) {
                    scope.isActive = checkIfActive();

                    if (changeTimeout) {
                        $timeout.cancel(changeTimeout);
                    }
                    changeTimeout = $timeout(function () {
                        scope.onChange({
                            $event: {
                                data: angular.element('#' + scope.typeaheadId)
                            }
                        });
                    }, 500);
                };
                scope.ngFocus = function (e) {
                    scope.isActive = true;
                    scope.onFocus({ $event: e });
                };
                scope.ngBlur = function (e) {
                    var model = getModel();
                    setValues(model);

                    if (multiselect && model.length > 0) {
                        scope.multiselectFlag = true;
                    }

                    scope.isActive = checkIfActive();
                    scope.onBlur({ $event: e });
                    if (model == undefined || model == null || model == '' || Object.getOwnPropertyNames(model).length === 0) {
                        scope.showInfoIcon = false;
                    } else {
                        scope.showInfoIcon = true;
                    }

                    // Needed to add $timeout for not showing error message before show lookup open.
                    $timeout(function () {
                        if (!showLookupTrigger) {
                            scope.validateRules();
                        } else {
                            showLookupTrigger = false;
                        }
                    }, 200);
                };
                var preventKeyArray = [37, 38, 39, 40];
                var showLookupTrigger = false;
                scope.ngKeyUp = function (e) {
                    if (multiselect && e.target.value == "") {
                        setModel([]);
                    }
                    if (!multiselect && e.target.value == "") {
                        setModel(null);
                    }
                    scope.showInfoIcon = false;
                    if (keyUpTimeout) {
                        $timeout.cancel(keyUpTimeout);
                    }
                    keyUpTimeout = $timeout(function () {
                        var key = 'which' in e ? e.which : e.keyCode;
                        if (!(preventKeyArray.indexOf(key) > -1)) {
                            scope.onKeyUp({ $event: e });
                        }
                    }, 500);
                    DuplicateData(scope.displayModel, e);
                };
                scope.ngKeyDown = function (e) {
                    scope.showInfoIcon = false;
                    if (keyDownTimeout) {
                        $timeout.cancel(keyDownTimeout);
                    }
                    keyDownTimeout = $timeout(function () {
                        var key = 'which' in e ? e.which : e.keyCode;
                        if (!(preventKeyArray.indexOf(key) > -1)) {
                            scope.onKeyDown({ $event: e });
                        }
                    }, 500);
                    var formattedData = getFormattedData(newOptionsSelected);
                    copyArray = angular.copy(formattedData).map(function (data) { return data.value.toLowerCase(); });
                    DuplicateData(scope.displayModel, e);
                };
                scope.ngKeyPress = function (e) {
                    if (keyPressTimeout) {
                        $timeout.cancel(keyPressTimeout);
                    }
                    keyPressTimeout = $timeout(function () {
                        var key = 'which' in e ? e.which : e.keyCode;
                        if (!(preventKeyArray.indexOf(key) > -1)) {
                            scope.onKeyPress({ $event: e });
                        }

                        if (e.charCode == 13) {
                            scope.onEnter({ $event: e });
                        }
                    }, 500);
                   DuplicateData(scope.displayModel, e);
                };

                scope.clickedOnIcon = function (e) {
                    scope.callbackOnIconClick({ $event: e });
                };

                scope.validateRules = function () {
                    var model = getModel();
                    if (scope.readonly == 'true') {
                        scope.isActive = true;
                        return;
                    }
                    if (scope.$eval(scope.isMandatory) && !scope.clienterror && (Object.keys(model).length == 0 || model == null || model == undefined || model == '' || model.id == 0)) {
                        scope.validate = true;
                        scope.errorMessage = $translate.instant("enterValueErrMsg");
                        scope.errorModel = $translate.instant("enterValueErrMsg");
                        return;
                    }
                    if (scope.clienterror) {
                        scope.validate = true;
                        scope.errorMessage = $translate.instant("clientErrMsg");
                        scope.errorModel = $translate.instant("clientErrMsg");

                        return;
                    }

                    scope.validate = false;
                    scope.errorMessage = null;
                    scope.errorModel = null;

                    if (scope.rules) {
                        var rules = scope.$eval(scope.rules);
                        var isFoundInvalid = false;
                        for (var i = 0; i < rules.length; i++) {
                            if (typeof rules[i] == 'object') {
                                if (eval((rules[i].rule).replace(/this/g, 'scope.displayModel'))) {
                                    scope.validate = true;
                                    scope.errorMessage = rules[i].error;
                                    scope.errorModel = rules[i].error;
                                    break;
                                }
                            }
                            else {
                                if (!isFoundInvalid) {
                                    RuleEngine.isValid(rules[i], function (e) {
                                        scope.validate = true;
                                        scope.errorMessage = $translate.instant(e.errorData.error);
                                        scope.errorModel = $translate.instant(e.errorData.error);
                                        isFoundInvalid = true;
                                    });
                                }
                                else {
                                    break;
                                }
                            }
                        }
                    }
                };

                var unbindValidate = scope.$watchGroup(['validate', 'clienterror', 'showinfo', 'showwarning'], function (newVal, oldVal) {
                    if (newVal[0] || newVal[1] != oldVal[1]) {
                        scope.validateRules();
                    }

                    if (newVal[2] != oldVal[2] || newVal[3] != oldVal[3]) {
                        if (scope.showinfo || scope.showwarning) {
                            element.hasClass('input-field').addClass('field-has-icon');
                        }
                        else {
                            element.hasClass('.input-field').removeClass('field-has-icon');
                        }
                    }
                });

                if (scope.clienterror) {
                    scope.validateRules();
                }

                function setModel(value, mode) {
                    value = !value ? (multiselect ? [] : {}) : value;
                    if (typeof scope.ngModel == "function") {
                        if (multiselect && mode == "autocomplete") {
                            var v = value;
                            value = ngModel.$modelValue;
                            value.push(v);
                        };
                        ngModel.$setViewValue(value);
                        ngModel.$render();
                        if (mode != "autocomplete") {
                            setValues(value);
                        };
                    } else {
                        if (multiselect && mode == "autocomplete") {
                            scope.ngModel.push(value)
                            }else if (multiselect === true) {	
                                if (typeof scope.ngModel == "string") {	
                                    scope.ngModel = value;	
                                } else {	
                                    if (scope.ngModel == value) {	
                                        value = angular.copy(value);	
                                    }	
                                    scope.ngModel.length = 0;	
                                    [].push.apply(scope.ngModel, value);	
                                };	
                            }
                             else {
                            scope.ngModel = value;
                        }
                        setValues(scope.ngModel);
                    }
                };

                function getModel() {
                    var model;
                    if (typeof scope.ngModel == "function") {
                        model = ngModel.$modelValue;
                    } else {
                        model = scope.ngModel;
                    }
                    return model;
                }

                function DuplicateData(editTest, event) {


                    if (editTest || event.keyCode == 188 || event.type == "keyup") {
                        var str_array = editTest.split(',');
                        for (var i = 0; i < str_array.length; i++) {
                            str_array[i] = str_array[i].replace(/^\s*/, "").replace(/\s*$/, "");
                        }
                        angular.forEach(str_array, function (val) {
                            var indexabc = copyArray.indexOf(val.toLowerCase());
                            selectedArrIndex = newOptionsSelected.map(function (data) { return data.value.toLowerCase(); }).indexOf(val.toLowerCase());

                            if (selectedArrIndex != -1 && newArr.indexOf(val) == -1) {
                                selectedArr.push(selectedArrIndex);
                                var ab = newOptionsSelected.splice(selectedArrIndex, 1)[0];
                                newArr.push(ab);
                                ab._$index = indexabc;
                            }
                        });
                        if (event) {
                            if (event.keyCode == 8 || event.keyCode == 46 || editTest.length == 3) {
                            angular.forEach(str_array, function (val, v1, v2) {
                                var isPresent = newArr.map(function (data) { return data.value; }).indexOf(val);
                                if (isPresent == -1) {
                                        for (var i = 0 ; i < newArr.length; i++) {
                                            if (newOptionsSelected.indexOf(newArr[i]) == -1 && v2.indexOf(newArr[i].value) == -1) {
                                                newOptionsSelected.splice(newArr[i]._$index, 0, newArr[i]);
                                            newArr.splice(i, 1);
                                        }
                                    }
                                }
                            });
                        }
                        }
                    }
                    else {
                        for (var i = 0 ; i < newArr.length; i++) {
                            if (newOptionsSelected.indexOf(newArr[i]) == -1) {
                                newOptionsSelected.splice(newArr[i]._$index, 0, newArr[i]);
                                newArr.splice(i, 1);
                            }
                        }
                    }
                }


                function getFormattedData(data) {
                    if (!data || data.length == 0) {
                        return [];
                    }
                    for (var i = 0; i < data.length; i++) {
                        var str = displayFormat;
                        try {
                            var parentSplit = str.split('{');
                            for (var j = 0; j < parentSplit.length; j++) {
                                var childSplit = parentSplit[j].split('}');
                                for (var k = 0; k < childSplit.length; k++) {
                                    if (childSplit[k].indexOf('.') > 0 || data[i].hasOwnProperty(childSplit[k])) {
                                        str = str.replace(new RegExp("{" + childSplit[k] + "}", "ig"), eval('data[i].' + childSplit[k]));
                                    }
                                }
                            }
                        }
                        catch (e) { }
                        data[i].value = str.trim();
                    }
                    return data;
                };

                var filterKeys;
                if (angular.isDefined(scope.filterkeys) && scope.filterkeys.trim().indexOf('[') == 0) {
                    filterKeys = scope.filterkeys ? scope.$eval(scope.filterkeys) : ['title'];
                }
                else {
                    filterKeys = scope.filterkeys ? scope.$eval('[' + scope.filterkeys + ']') : ['title'];
                }

                //set optionFormat displayFormat related to filterKeys incase undefined
                scope.filterKeys = scope.$eval(scope.filterkeys);
                function getOpFormate() {
                    var opFor = "";
                    for (var i = 0; i < filterKeys.length; i++) {
                        opFor = opFor + "{" + filterKeys[i] + "} ";
                    }
                    if (opFor == "") {
                        return "{title}";
                    }
                    opFor.trim();
                    return opFor;
                }

                function getDispFormate() {
                    var opDisp = "<span>";
                    for (var i = 0; i < filterKeys.length; i++) {
                        opDisp = opDisp + "{" + filterKeys[i] + "} ";
                    }
                    if (opDisp == "<span>") {
                        return "{title}";
                    }
                    opDisp.trim();
                    opDisp = opDisp + "</span>";
                    return opDisp;
                }

                var optionFormat = scope.optionformat ? scope.optionformat : getDispFormate();
                var displayFormat = scope.displayformat ? scope.displayformat : getOpFormate();

                function getDisplayData(data) {
                    if (data == undefined || data == null || data == '') {
                        return '';
                    }
                    var str = displayFormat, evalResult, evaluatedKeysCounter = 0, nullKeysCounter = 0;
                    try {
                        var parentSplit = str.split('{');
                        for (var j = 0; j < parentSplit.length; j++) {
                            var childSplit = parentSplit[j].split('}');
                            for (var k = 0; k < childSplit.length; k++) {
                                if (childSplit[k].indexOf('.') > 0 || data.hasOwnProperty(childSplit[k])) {
                                    evalResult = eval('data.' + childSplit[k]);
                                    evaluatedKeysCounter++;
                                    if (evalResult == undefined || evalResult == null || (evalResult + '').trim().length == 0) {
                                        evalResult = '';
                                        nullKeysCounter++;
                                    }
                                    str = str.replace(new RegExp("{" + childSplit[k] + "}", "ig"), evalResult);
                                }
                            }
                        }
                        if (evaluatedKeysCounter == nullKeysCounter) {
                            str = '';
                        }
                    }
                    catch (e) {
                    }
                    return str === undefined || str === null || str === 'undefined' || str === 'null' ? '' : str;
                }
                //Function return the array of proprrties Input:"{abc} {dfg}"; Output:["abc","dfg"]
                function getFormatedArray(formatString) {
                    var str = formatString.trim();
                    try {
                        var parentSplit = str.split('{'), propArray = [];
                        for (var j = 0; j < parentSplit.length; j++) {
                            var childSplit = parentSplit[j].split('}');
                            if (j != 0) {
                                propArray.push(childSplit[0].trim());
                            }
                        }
                    }
                    catch (e) { }
                    return propArray === undefined || propArray === null || propArray === 'undefined' || propArray === 'null' ? '' : propArray;
                };
                var formatedArray = getFormatedArray(displayFormat);

                var delimiterVal = null;
                if (multiselect == true) {
                    delimiterVal = /(,|;)\s*/;
                }
                var unbindOptions;
                $timeout(function () {
                    unbindOptions = scope.$watch('options', function (newOptions) {
                        try {
                            newOptionsSelected = newOptions;
                            var formattedData = getFormattedData(newOptionsSelected);
                            copyArray = angular.copy(formattedData).map(function (data) { return data.value.toLowerCase(); });
                            if (!readonly) {
                                if (!$('#' + scope.typeaheadId).devbridgeAutocomplete()) {
                                    var autoCompleteField = $('#' + scope.typeaheadId).devbridgeAutocomplete({
                                        filterKeys: filterKeys,
                                        optionFormat: optionFormat,
                                        displayFormat: displayFormat,
                                        lookup: formattedData,
                                        width: scope.width,
                                        maxHeight: scope.height,
                                        multiselect: multiselect,
                                        showLookup: showLookup,
                                        addnew: addnew,
                                        minChars: minCharForTrigger,
                                        delimiter: delimiterVal,
                                        showNoSuggestionNotice: showNoSuggestion,
                                        noSuggestionNotice: noSuggestionNotice,
                                        loading: loading,
                                        minMultiselect: minMultiselect,
                                        showLookupKey: showLookupKey,
                                        addNewKey: addNewKey,
                                        onSelect: function (suggestion) {
                                            angular.element('#' + scope.labelFortypeaheadId).trigger('click');
                                            scope.$evalAsync(function () {
                                                if (multiselect == true) {
                                                    scope.displayModel = "";
                                                    setModel(suggestion, 'autocomplete');
                                                    angular.forEach(getModel(), function (val) {
                                                        scope.displayModel = scope.displayModel + val.value + ', ';
                                                    });
                                                    DuplicateData(scope.displayModel);
                                                } else {
                                                    scope.displayModel = getDisplayData(suggestion);
                                                    setModel(suggestion, 'autocomplete');
                                                }
                                                $timeout(function () {
                                                    scope.onSelect(suggestion);
                                                });
                                            });
                                        },
                                        onClickAddNew: function (e) {
                                            if (addnew) {
                                                scope.callAddNew({ $event: e });
                                                document.body.style.height = document.body.scrollHeight + 2 + "px";
                                                $timeout(function () {
                                                    window.scrollTo(0, (document.body.scrollTop + 1));
                                                    window.scrollTo(0, (document.body.scrollTop - 1));
                                                    document.body.style.height = "";
                                                });
                                            }
                                        },
                                        onClickShowLookup: function (e) {
                                            if (showLookup) {
                                                scope.openLookup();
                                            }
                                        }
                                    }).devbridgeAutocomplete();

                                    $(autoCompleteField.element).focus(function (e) {
                                        alignLookUp.call(this, e);
                                        var drp = $window.lastOpenedDropdownConfig;
                                        if (drp && !drp.activates.is(":hidden") && !angular.element(this).closest(drp.activates).length) { // focused textBox shuld not be within the Materlized dropDown thenonly close the Materlized dropDown.
                                            //Materlized dropdown should be closed on focus on autoComplatetextbox
                                            drp.origin.trigger('close');
                                        }
                                    }).keyup(function (e) {
                                        alignLookUp.call(this, e);
                                    });

                                    //Closing the suggestions on scroll to prevent overlapping issue
                                    angular.element($window).scroll(function (e) {
                                        if (autoCompleteField.visible) {
                                            autoCompleteField.hide();
                                        }
                                    });
                                    //End of closing the suggestions on scroll to prevent overlapping issue

                function alignLookUp(e) {
                    var _this = $(this),
                        scrollTop = $("html").scrollTop() || $("body").scrollTop(),
                        elPosition = _this.offset(),
                        elHeight = _this.outerHeight(),
                        lookup = $(autoCompleteField.suggestionsContainer),
                        lookupPosition = lookup.offset(),
                        lookupHeight = lookup.outerHeight(),
                        isOutOfBound = ((elPosition.top - scrollTop) + elHeight + lookupHeight) > window.innerHeight;

                    lookup.css("opacity", 0);
                    if (isOutOfBound) { // if lookup list is going out of window
                        if (autoCompleteField.options.orientation != "top") {
                            autoCompleteField.options.orientation = "top";
                            autoCompleteField.fixPosition();
                        }
                    } else {
                        if (autoCompleteField.options.orientation == "top") {
                            autoCompleteField.options.orientation = "bottom";
                            autoCompleteField.fixPosition();
                        }
                    }
                    lookup.css("opacity", 1);
                                    }
                                } else {
                                    $('#' + scope.typeaheadId).devbridgeAutocomplete().setLookup(getFormattedData(newOptions));
                                }
                            }
                        }
                        catch (e) { }
                    });
                    var unbindLoading = scope.$watch('loading', function (newVal, oldVal) {
                        $('#' + scope.typeaheadId).devbridgeAutocomplete().setLoading(newVal);
                    });
                });

                scope.openLookup = function () {
                    showLookupTrigger = true;
                    var len = newArr.length;
                    for (var i = 0 ; i < len; i++) {
                        if (newOptionsSelected.indexOf(newArr[i]) == -1) {
                            newOptionsSelected.splice(newArr[i]._$index, 0, newArr[i]);
                            newArr.splice(i, 1);
                        }
                    }
                    if (scope.readonly === 'true' && !scope.mutiselectVal[1]) return false;

                    if ($.isFunction(scope.lookupOpen)) {
                        scope.lookupOpen();
                    }
                    var lookupConfig = {
                        modelData: getModel(),
                        config: {
                            mutliselect: multiselect,
                            displayProperties: formatedArray,
                            id:scope.typeaheadId + '_listContainer',
                            options: scope.options,
                            addnew: addnew,
                            onKeyUp: attrs.searchtextKeyUp ? scope.searchtextKeyUp : '',
                            titleOfModel: scope.titleofmodel,
                            defaultSelectOption: scope.defaultselectoption,
                            defaultSelectionText: scope.defaultselectiontext,
                            selectTypeOption: scope.selecttypeoption,
                            readonly: (scope.readonly == undefined || scope.readonly == null) ? false : scope.readonly == "true" ? true : false,
                            isMandatory: scope.isMandatory,
                            isDefaultMandatory: scope.isDefaultMandatory,
                            errorModel: scope.errorModel,
                            minMultiselect: minMultiselect,
                            label: scope.label,
                            isLazyLoadingEnabled: scope.isLazyLoadingEnabled,
                            stopFilterOnKey: scope.stopFilterOnKey,
                            searchCount: scope.searchCount,
                            requireLoader: scope.requireLoader,
			    enablePagination: attrs.enablePagination ? scope.enablePagination : ''
                        }
                    }
                    $timeout(function () {
                        lookup.open(lookupConfig, function (response) {
                            if (response.addnew) {
                                if ($.isFunction(scope.callAddNew)) {
                                    scope.callAddNew();
                                }
                            }
                            setModel(response.result);
                            scope.defaultselectiontext = response.defaultSelectionText;
                            scope.selecttypeoption = response.selectTypeOption;
                            scope.errorModel = response.errorModel;
                            scope.validateRules();
                            if ($.isFunction(scope.lookupHide)) {
                                scope.lookupHide({ data: response });
                            }
                        });
                        lookup.onScrollEnd(function () {
                            scope.onScrollEnd({});
                        });
                        lookup.onScrollTop(function () {
                            scope.onScrollTop({});
                        });
                        lookup.onScrollInProgress(function () {
                            scope.onScrollInProgress({});
                        });
                    });
                }
                scope.$on('$destroy', function () {
                    if ($('#' + scope.typeaheadId).devbridgeAutocomplete()) {
                        $('#' + scope.typeaheadId).devbridgeAutocomplete().dispose();
                    }
                    destroyEverythingElse();
                });

                scope.multiselectFlag = false;

                function setValues(newVal) {
                     scope.mutiselectVal = [""];
                    if (angular.isDefined(newVal) && newVal != null) {
                        var valuee = "",
                        totalEl = newVal.length;
                        if (multiselect == true) {
                            if (newVal.length > 0) {

                                if (minMultiselect.event) {
                                    if ((newVal.length == minMultiselect.value) && (getModel().length == minMultiselect.value)) {
                                        scope.validateRules();
                                    }
                                }

                                if (showLookup) {
                                    scope.multiselectFlag = true;
                                }
                                if (scope.defaultselectoption) {
                                    if (!scope.defaultselectiontext) {
                                        scope.defaultselectiontext = getDisplayData(newVal[0]).replace(/(^\s*)|(\s*$)/gi, "");
                                        scope.mutiselectVal = [scope.defaultselectiontext + " (Default)"];
                                    }
                                    for (var i = 0; i < newVal.length; i++) {
                                        if (newVal[i].name === scope.defaultselectiontext) {
                                            scope.mutiselectVal = [getDisplayData(newVal[i]) + " (Default)"]; break;
                                        }
                                    }

                                } else {
                                    scope.mutiselectVal = [getDisplayData(newVal[0])];
                                }

                                if (totalEl > 1) {
                                    scope.mutiselectVal.push("+" + (totalEl - 1) + " More");
                                }
                            } else {
                                scope.multiselectFlag = false;
                            }
                            for (var i = 0; i < totalEl; i++) {
                                if (valuee && totalEl > 1) {
                                    valuee += "," + getDisplayData(newVal[i]);
                                }
                                else {
                                    valuee += getDisplayData(newVal[i])
                                }
                            }

                        }

                        else {
                            valuee = getDisplayData(newVal);
                            scope.showInfoIcon = (valuee == '' || scope.isSupplier) ? false: true;
                        }
                        scope.displayModel = valuee.trim();
                    }
                    scope.isActive = checkIfActive();
                    checkInfoToolTip();
                }

                var unbindNumber = scope.$watch("ngModel", function (newVal) {
                    if (typeof scope.ngModel == "function") {
                        setValues(ngModel.$modelValue);
                    } else {
                        setValues(newVal);
                    }
                });
                function destroyEverythingElse() {
                    element.off(); // deregister all event handlers
                    $('#' + scope.typeaheadId).off();
                    $('#' + scope.labelFortypeaheadId).off();
                    if (unbindFocus)
                        unbindFocus();
                    if (angular.isFunction(unbindOptions))
                        unbindOptions();
                    if (unbindNumber)
                        unbindNumber();
                    // Removing contents of HTML from DOM
                    $('#' + scope.typeaheadId).remove();
                    $('#' + scope.labelFortypeaheadId).remove();
                }
            },
            templateUrl: 'shared/directives/uiElements/smartTypeahead/smartTypeaheadTemplate.html'
        };
    }]);
})();
